import { useState, useEffect } from "react";

const API = process.env.REACT_APP_API_URL || "";

const fmt = (v, decimals = 2) => v != null ? Number(v).toFixed(decimals) : "\u2014";
const fmtB = (v) => v != null ? `$${(v / 1e9).toFixed(2)}B` : "\u2014";
const fmtPct = (v) => v != null ? `${(v * 100).toFixed(2)}%` : "\u2014";

// Column definitions: [header, dataKey, formatter]
const COLUMNS = [
  ["Ticker",     "ticker",              null],
  ["List",       "list",                null],
  ["Score",      "final_score",         null],
  ["Price",      "price",               null],
  ["Buy Zone",   "buyzone",             null],
  ["200 SMA",    "sma_200",             null],
  ["vs SMA",     "price_vs_sma_200",    null],
  ["Mkt Cap",    "market_cap",          null],
  ["52wk Hi",    "year_high",           null],
  ["52wk Lo",    "year_low",            null],
  ["Revenue",    "revenue",             null],
  ["Net Inc",    "net_income",          null],
  ["EBITDA",     "ebitda",              null],
  ["Op CF",      "operating_cash_flow", null],
  ["Capex",      "capital_expenditure", null],
  ["Comp FCF",   "computed_fcf",        null],
  ["FCF Yield",  "computed_fcf_yield",  null],
  ["B/M",        "computed_bm",         null],
  ["TEV",        "computed_tev",        null],
  ["ROA",        "computed_roa",        null],
  ["Asset Gr%",  "computed_asset_growth",null],
  ["EBITDA Gr%", "computed_ebitda_growth",null],
  ["Pr Range%",  "computed_price_range", null],
  ["Mom 6m%",    "computed_momentum_6m", null],
  ["Flags",      "_flags",              null],
];

export default function SignalsRawData() {
  const [data, setData]               = useState([]);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [expanded, setExpanded]       = useState(null);
  const [sortCol, setSortCol]         = useState("final_score");
  const [sortDir, setSortDir]         = useState("desc");

  useEffect(() => {
    setLoading(true);
    const url = flaggedOnly ? `${API}/signals/flags` : `${API}/signals/raw`;
    fetch(url)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [flaggedOnly]);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("desc");
    }
  };

  const getSortIcon = (col) => {
    if (sortCol !== col) return " \u2195";
    return sortDir === "asc" ? " \u2191" : " \u2193";
  };

  const sortedData = [...data].sort((a, b) => {
    let aVal = a[sortCol];
    let bVal = b[sortCol];
    if (aVal == null) aVal = sortDir === "asc" ? Infinity : -Infinity;
    if (bVal == null) bVal = sortDir === "asc" ? Infinity : -Infinity;
    if (typeof aVal === "string" && typeof bVal === "string") {
      return sortDir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    const aNum = typeof aVal === "string" ? parseFloat(aVal) || 0 : aVal;
    const bNum = typeof bVal === "string" ? parseFloat(bVal) || 0 : bVal;
    return sortDir === "asc" ? aNum - bNum : bNum - aNum;
  });

  const exportCSV = () => {
    const headers = COLUMNS.map(c => c[0]);
    const rows = sortedData.map(r => [
      r.ticker,
      r.list ?? "search",
      r.final_score ?? "",
      r.price ?? "",
      r.buyzone > 1.0 ? r.buyzone : "",
      r.sma_200 ?? "",
      r.price_vs_sma_200 ?? "",
      r.market_cap ?? "",
      r.year_high ?? "",
      r.year_low ?? "",
      r.revenue ?? "",
      r.net_income ?? "",
      r.ebitda ?? "",
      r.operating_cash_flow ?? "",
      r.capital_expenditure ?? "",
      r.computed_fcf ?? "",
      r.computed_fcf_yield ?? "",
      r.computed_bm ?? "",
      r.computed_tev ?? "",
      r.computed_roa ?? "",
      r.computed_asset_growth ?? "",
      r.computed_ebitda_growth ?? "",
      r.computed_price_range ?? "",
      r.computed_momentum_6m ?? "",
      r.flag_notes ?? "",
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `axiom_signals_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ padding: 48, color: "#9a99a2" }}>Loading raw data...</div>;
  if (error)   return <div style={{ padding: 48, color: "#E24B4A" }}>Error: {error}</div>;

  const thStyle = (idx) => ({
    padding: "8px 10px", textAlign: "left",
    borderBottom: "1px solid #2a2a3e", whiteSpace: "nowrap",
    fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5,
    position: "sticky", top: 0, zIndex: idx === 0 ? 20 : 10,
    background: "#1a1a2e", cursor: "pointer", userSelect: "none",
    ...(idx === 0 ? { left: 0 } : {}),
  });

  return (
    <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#e2e0d8",
                  fontFamily: "'Inter', sans-serif", padding: "24px 32px" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, color: "#5f5e6e", letterSpacing: 2, textTransform: "uppercase", marginBottom: 4 }}>
            Axiom Intelligence
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "#e8e6de" }}>
            Raw Factor Data
          </h1>
          <div style={{ fontSize: 12, color: "#5f5e6e", marginTop: 4 }}>
            FMP source data &middot; computed intermediates &middot; IV&V flags &middot; {data.length} tickers
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <a href="/signals" style={{
            padding: "6px 14px", borderRadius: 4, fontSize: 11, textDecoration: "none",
            border: "1px solid #2a2a3e", background: "#1a1a2e", color: "#9a99a2",
          }}>&larr; Scores</a>
          <button
            onClick={() => {
              setLoading(true);
              fetch(`${API}/admin/signals/run`, {
                method: "POST",
              })
                .then(() => {
                  // Reload data after screener completes
                  const url = flaggedOnly ? `${API}/signals/flags` : `${API}/signals/raw`;
                  return fetch(url).then(r => r.json());
                })
                .then(d => { setData(d); setLoading(false); })
                .catch(e => { setError(e.message); setLoading(false); });
            }}
            style={{
              padding: "6px 14px", borderRadius: 4, fontSize: 11, cursor: "pointer",
              border: "1px solid #1D9E75", background: "#1D9E7522", color: "#1D9E75",
            }}
          >
            Refresh Prices
          </button>
          <button
            onClick={exportCSV}
            style={{
              padding: "6px 14px", borderRadius: 4, fontSize: 11, cursor: "pointer",
              border: "1px solid #2a2a3e", background: "#1a1a2e", color: "#9a99a2",
            }}
          >
            Export CSV
          </button>
          <button
            onClick={() => setFlaggedOnly(!flaggedOnly)}
            style={{
              padding: "6px 14px", borderRadius: 4, fontSize: 11, cursor: "pointer",
              border: flaggedOnly ? "1px solid #E24B4A" : "1px solid #2a2a3e",
              background: flaggedOnly ? "#E24B4A22" : "#1a1a2e",
              color: flaggedOnly ? "#E24B4A" : "#9a99a2",
            }}
          >
            {flaggedOnly ? "\u26A0 Flagged Only" : "All Tickers"}
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", maxHeight: "calc(100vh - 140px)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#1a1a2e", color: "#5f5e6e" }}>
              {COLUMNS.map(([h, key], idx) => (
                <th key={h} onClick={() => handleSort(key)} style={thStyle(idx)}>
                  {h}{getSortIcon(key)}
                </th>
              ))}
            </tr>
          </thead>
          {sortedData.map((row, i) => {
            const hasFlag = row.fcf_yield_flagged || row.bm_flagged ||
                            row.roa_flagged || row.momentum_flagged || row.inv_dummy_flagged;
            const isExpanded = expanded === row.ticker;
            const inBuyzone = row.in_buyzone;
            const deepBuyzone = inBuyzone && row.price != null && row.price <= row.buyzone * 0.85;
            const nearBuyzone = !inBuyzone && row.buyzone > 1.0 && row.price != null && row.price <= row.buyzone * 1.10;
            const baseBg = isExpanded ? "#1a1a2e" : hasFlag ? "#2a1a1a" : i % 2 === 0 ? "#12121f" : "#151525";
            const rowBg = deepBuyzone ? "#2a0a0a" : inBuyzone ? "#0a2a1a" : nearBuyzone ? "#2a2a0a" : baseBg;
            return (
              <tbody key={row.ticker}>
                  <tr
                    onClick={() => setExpanded(isExpanded ? null : row.ticker)}
                    style={{
                      cursor: "pointer",
                      background: rowBg,
                      borderBottom: "1px solid #1a1a2e",
                      borderLeft: deepBuyzone ? "3px solid #E24B4A" : inBuyzone ? "3px solid #1D9E75" : nearBuyzone ? "3px solid #C9A227" : "3px solid transparent",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "#1a1a2e"}
                    onMouseLeave={e => e.currentTarget.style.background = rowBg}
                  >
                    <td style={{ padding: "6px 10px", fontWeight: 600, color: deepBuyzone ? "#E24B4A" : inBuyzone ? "#1D9E75" : nearBuyzone ? "#C9A227" : "#e8e6de", position: "sticky", left: 0, zIndex: 5, background: rowBg }}>{row.ticker}</td>
                    <td style={{ padding: "6px 10px" }}>
                      <span style={{ fontSize: 10, color: row.list === "watch" ? "#BA7517" : row.list === "long_invest" ? "#7B8CDE" : "#1D9E75", background: row.list === "watch" ? "#2a2a0a" : row.list === "long_invest" ? "#1a1a2e" : "#0a2a1a", padding: "2px 6px", borderRadius: 3, textTransform: "uppercase", fontWeight: 600 }}>{row.list === "long_invest" ? "LONG" : (row.list || "search")}</span>
                    </td>
                    <td style={{ padding: "6px 10px", color: row.final_score >= 70 ? "#1D9E75" : row.final_score >= 40 ? "#BA7517" : "#E24B4A", fontWeight: 700 }}>
                      {row.final_score ? Math.round(row.final_score) : "\u2014"}
                    </td>
                    <td style={{ padding: "6px 10px", color: deepBuyzone ? "#E24B4A" : inBuyzone ? "#1D9E75" : nearBuyzone ? "#C9A227" : "#e2e0d8", fontWeight: (inBuyzone || nearBuyzone) ? 700 : 400 }}>{fmt(row.price)}</td>
                    <td style={{ padding: "6px 10px", color: deepBuyzone ? "#E24B4A" : inBuyzone ? "#1D9E75" : nearBuyzone ? "#C9A227" : "#9a99a2", fontWeight: (inBuyzone || nearBuyzone) ? 700 : 400 }}>
                      {row.buyzone > 1.0 ? `$${fmt(row.buyzone)}` : "\u2014"}
                    </td>
                    <td style={{ padding: "6px 10px" }}>{fmt(row.sma_200)}</td>
                    <td style={{ padding: "6px 10px", color: row.price_vs_sma_200 === "below" ? "#1D9E75" : row.price_vs_sma_200 === "above" ? "#9a99a2" : "#5f5e6e", fontWeight: 600 }}>
                      {row.price_vs_sma_200 === "below" ? "\u25BC Below" : row.price_vs_sma_200 === "above" ? "\u25B2 Above" : "\u2014"}
                    </td>
                    <td style={{ padding: "6px 10px" }}>{fmtB(row.market_cap)}</td>
                    <td style={{ padding: "6px 10px" }}>{fmt(row.year_high)}</td>
                    <td style={{ padding: "6px 10px" }}>{fmt(row.year_low)}</td>
                    <td style={{ padding: "6px 10px" }}>{fmtB(row.revenue)}</td>
                    <td style={{ padding: "6px 10px" }}>{fmtB(row.net_income)}</td>
                    <td style={{ padding: "6px 10px" }}>{fmtB(row.ebitda)}</td>
                    <td style={{ padding: "6px 10px" }}>{fmtB(row.operating_cash_flow)}</td>
                    <td style={{ padding: "6px 10px" }}>{fmtB(row.capital_expenditure)}</td>
                    <td style={{ padding: "6px 10px" }}>{fmtB(row.computed_fcf)}</td>
                    <td style={{ padding: "6px 10px", color: row.fcf_yield_flagged ? "#E24B4A" : "#e2e0d8" }}>
                      {fmtPct(row.computed_fcf_yield)}
                      {row.fcf_yield_flagged ? " \u26A0" : ""}
                    </td>
                    <td style={{ padding: "6px 10px", color: row.bm_flagged ? "#E24B4A" : "#e2e0d8" }}>
                      {fmt(row.computed_bm, 3)}{row.bm_flagged ? " \u26A0" : ""}
                    </td>
                    <td style={{ padding: "6px 10px" }}>{fmtB(row.computed_tev)}</td>
                    <td style={{ padding: "6px 10px", color: row.roa_flagged ? "#E24B4A" : "#e2e0d8" }}>
                      {fmtPct(row.computed_roa)}{row.roa_flagged ? " \u26A0" : ""}
                    </td>
                    <td style={{ padding: "6px 10px" }}>{fmt(row.computed_asset_growth)}%</td>
                    <td style={{ padding: "6px 10px" }}>{fmt(row.computed_ebitda_growth)}%</td>
                    <td style={{ padding: "6px 10px" }}>{fmt(row.computed_price_range)}%</td>
                    <td style={{ padding: "6px 10px", color: row.momentum_flagged ? "#E24B4A" : "#e2e0d8" }}>
                      {fmt(row.computed_momentum_6m)}%{row.momentum_flagged ? " \u26A0" : ""}
                    </td>
                    <td style={{ padding: "6px 10px", color: hasFlag ? "#E24B4A" : "#1D9E75", fontWeight: 600 }}>
                      {hasFlag ? "\u26A0 Flagged" : "\u2713 Clean"}
                    </td>
                  </tr>
                  {isExpanded && row.flag_notes && (
                    <tr style={{ background: "#1a0a0a" }}>
                      <td colSpan={24} style={{ padding: "8px 16px", color: "#E24B4A", fontSize: 11 }}>
                        <strong>IV&V Notes:</strong> {row.flag_notes}
                      </td>
                    </tr>
                  )}
                </tbody>
              );
            })}
        </table>
      </div>

      {data.length === 0 && (
        <div style={{ textAlign: "center", color: "#5f5e6e", marginTop: 80, fontSize: 14 }}>
          No raw data yet. Run the screener first.
        </div>
      )}
    </div>
  );
}
